import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Inbox, Search, X, AlertCircle, RefreshCw, Eye, EyeOff, Trash2, Building2,
  CheckCircle2, ArrowDownLeft, ArrowUpRight, ArrowUp, ArrowDown,
} from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import { useAuth } from '../hooks/useAuth';
import { useEmailsPendentes } from '../hooks/useEmailsPendentes';
import useBulkSelection from '../components/shared/useBulkSelection';
import BulkActionBar from '../components/shared/BulkActionBar';
import SelectAllCheckbox from '../components/shared/SelectAllCheckbox';
import BulkVincularEmpresaModal from '../components/BulkVincularEmpresaModal';
import EmailsPendentesModal, { computeVinculoPreview } from '../components/EmailsPendentesModal';

/**
 * Inbox 2.0 — Página de Triage com Matriz de Proveniência (ADR-029 / Fase 3).
 *
 * Bancada primária de triage da fila `emails_gmail` (acao_humana='pendente').
 * É camada de LEITURA + reorganização + DISPARO das ações que já existem — não é
 * caminho de escrita paralelo (SPEC §9.1, P25, D-11). ZERO backend novo.
 *
 * O modal "Inbox de Aprendizado" (EmailsPendentesModal) segue intacto como
 * drill-down (SPEC §10).
 */

// Teto alto p/ ler a fila inteira sem o cap silencioso de 100 do modal (SPEC §8).
const TRIAGE_LIMIT = 1000;

// Virtualização manual (sem dependência nova — fallback sancionado pela SPEC §8).
const ROW_H = 48;
const HEADER_H = 38;
const OVERSCAN = 8;

// Template de colunas compartilhado entre cabeçalho e linhas (alinhamento).
const GRID =
  'grid grid-cols-[36px_44px_minmax(220px,2.2fr)_minmax(160px,1.3fr)_minmax(160px,1.3fr)_minmax(150px,1.4fr)_148px_104px_64px_92px_132px] items-center';
const GRID_MIN_W = 1200;

// ── Funções puras (testáveis) ───────────────────────────────────────────────

const PENDENCIAS = {
  VINCULAR: { label: 'Vincular', order: 0, badge: 'bg-red-100 text-red-800' },
  CONFIRMAR: { label: 'Confirmar', order: 1, badge: 'bg-amber-100 text-amber-800' },
  REVISAR: { label: 'Revisar', order: 2, badge: 'bg-slate-100 text-slate-600' },
};

/**
 * Pendência principal por linha (SPEC §4). Determinística.
 * - empresa_id nulo                          → VINCULAR (sub: contato conhecido | frio)
 * - empresa_id != nulo + status 'ai_sugerido' → CONFIRMAR
 * - empresa_id != nulo + status 'pendente'    → REVISAR
 * - fallback                                  → REVISAR
 * Sobre os 289 ao vivo: 141 / 128 / 20, sem órfão.
 */
export function pendenciaPrincipal(email) {
  if (!email?.empresa_id) {
    return { key: 'VINCULAR', sub: email?.contato_id ? 'contato conhecido' : 'frio' };
  }
  if (email.classificacao_status === 'ai_sugerido') return { key: 'CONFIRMAR', sub: null };
  if (email.classificacao_status === 'pendente') return { key: 'REVISAR', sub: null };
  return { key: 'REVISAR', sub: null };
}

const ORIGEM_MAP = {
  'gmail-sync:contato_email': 'Contato conhecido',
  'gmail-sync:rpc:recipient_domain': 'Domínio do destinatário',
  'gmail-sync:rpc:from_domain': 'Domínio do remetente',
  'gmail-sync:rpc:from_website': 'Site da empresa',
  'gmail-sync:cascade:domain': 'Cascata de domínio',
};
const ORIGEM_OPTIONS = [
  'Desconhecido',
  'Contato conhecido',
  'Domínio do destinatário',
  'Domínio do remetente',
  'Site da empresa',
  'Cascata de domínio',
];

/**
 * Origem do vínculo derivada de `classificado_por` (SPEC §5). Proveniência real;
 * NUNCA `sugestoes_ia` (vazio em 289/289). Fallback: texto bruto (nunca quebra).
 */
export function origemVinculo(classificadoPor) {
  if (!classificadoPor) return 'Desconhecido';
  return ORIGEM_MAP[classificadoPor] || classificadoPor;
}

// Colunas ordenáveis (todas menos seleção e "Ações"). Client-side, sobre os
// dados já carregados — zero backend.
const SORTABLE = ['dir', 'assunto', 'remetente', 'destinatario', 'empresa', 'origem', 'pendencia', 'conf', 'data'];

/**
 * Valor de ordenação por coluna. PURO. Categóricas (pendencia, origem, dir)
 * ordenam pela CHAVE numérica/semântica — NUNCA pelo rótulo PT.
 */
function sortValue(key, e) {
  switch (key) {
    case 'dir': return e.direcao;
    case 'assunto': return (e.subject || '').toLowerCase();
    case 'remetente': return (e.from_email || '').toLowerCase();
    case 'destinatario': return (e.to_emails?.[0] || '').toLowerCase();
    case 'empresa': return (e.empresas?.empresa || '').toLowerCase();
    case 'origem': {
      const idx = ORIGEM_OPTIONS.indexOf(origemVinculo(e.classificado_por));
      return idx === -1 ? ORIGEM_OPTIONS.length : idx; // balde desconhecido por último
    }
    case 'pendencia': return PENDENCIAS[pendenciaPrincipal(e).key].order;
    case 'conf': return e.classificacao_confianca;
    case 'data': return new Date(e.data_envio || 0).getTime();
    default: return null;
  }
}

const isEmptySort = (v) => v == null || v === '';

/**
 * Comparador de uma coluna. Nulos/vazios SEMPRE por último (em asc E desc).
 * Numéricos por subtração; strings lexicograficamente. PURO.
 */
function sortComparator(sort, a, b) {
  const va = sortValue(sort.key, a);
  const vb = sortValue(sort.key, b);
  const ea = isEmptySort(va);
  const eb = isEmptySort(vb);
  if (ea && eb) return 0;
  if (ea) return 1; // a vazio → depois (independe da direção)
  if (eb) return -1;
  let cmp;
  if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
  else cmp = String(va) < String(vb) ? -1 : String(va) > String(vb) ? 1 : 0;
  return sort.dir === 'desc' ? -cmp : cmp;
}

// Mapa domínio(lower)→empresa, só com domínios que têm empresa vinculada.
// Mesma derivação do modal (Fase 0) — alimenta a resolução do VINCULAR em lote.
function buildDominioEmpresaMap(dominiosEmpresa) {
  const m = new Map();
  (dominiosEmpresa || []).forEach((d) => {
    if (!d?.dominio || !d?.empresa_id) return;
    const key = String(d.dominio).toLowerCase().trim();
    if (!m.has(key)) {
      m.set(key, { empresa_id: d.empresa_id, empresa_nome: d.empresas?.empresa || '(empresa)' });
    }
  });
  return m;
}

function dataRelativa(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const meses = Math.floor(d / 30);
  return `${meses}mês`;
}

// ── Linha da matriz ─────────────────────────────────────────────────────────

const TriageRow = ({ email, style, selected, onToggle, onAbrir, onConfirmar, onIgnorar, onLixo }) => {
  const pend = pendenciaPrincipal(email);
  const pcfg = PENDENCIAS[pend.key];
  const empresaNome = email.empresas?.empresa || '—';
  const isSugerida = email.classificacao_status === 'ai_sugerido';
  const isOrigemDuvidosa = !!email.empresa_id && email.classificacao_status === 'pendente';
  const tos = Array.isArray(email.to_emails) ? email.to_emails : [];
  const destinatario = tos.length === 0 ? '—' : tos.length === 1 ? tos[0] : `${tos[0]} (+${tos.length - 1})`;
  const entrada = email.direcao === 'entrada';
  const podeConfirmar = !!email.empresa_id && isSugerida;
  const conf =
    isSugerida && email.classificacao_confianca != null
      ? `${Math.round(email.classificacao_confianca * 100)}%`
      : '—';

  return (
    <div
      style={style}
      className={`${GRID} px-1 border-b border-slate-100 text-xs ${
        selected ? 'bg-blue-50/50' : 'bg-white hover:bg-slate-50'
      }`}
    >
      {/* 1 seleção */}
      <div className="flex justify-center">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggle(email.id)}
          aria-label="Selecionar email"
          className="w-4 h-4 cursor-pointer accent-blue-600 rounded"
        />
      </div>
      {/* 2 direção */}
      <div className="flex justify-center" title={entrada ? 'Entrada' : 'Saída'}>
        {entrada ? (
          <ArrowDownLeft size={15} className="text-emerald-600" />
        ) : (
          <ArrowUpRight size={15} className="text-blue-600" />
        )}
      </div>
      {/* 3 assunto (drill-down) */}
      <button
        onClick={() => onAbrir(email)}
        className="text-left truncate pr-2 font-medium text-slate-800 hover:text-blue-700 hover:underline"
        title={email.subject || '(sem assunto)'}
      >
        {email.subject || '(sem assunto)'}
      </button>
      {/* 4 remetente */}
      <div className="truncate pr-2 text-slate-600" title={email.from_email || ''}>
        {email.from_email || '—'}
      </div>
      {/* 5 destinatário */}
      <div className="truncate pr-2 text-slate-600" title={tos.join(', ')}>
        {destinatario}
      </div>
      {/* 6 empresa */}
      <div className="truncate pr-2 flex items-center gap-1 min-w-0">
        <span className={`truncate ${empresaNome === '—' ? 'text-slate-400' : 'text-slate-800 font-medium'}`}>
          {empresaNome}
        </span>
        {isSugerida && (
          <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">sugerida</span>
        )}
        {isOrigemDuvidosa && (
          <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-500">origem?</span>
        )}
      </div>
      {/* 7 origem do vínculo */}
      <div className="truncate pr-2 text-[11px] text-slate-500" title={email.classificado_por || 'Desconhecido'}>
        {origemVinculo(email.classificado_por)}
      </div>
      {/* 8 pendência principal */}
      <div>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${pcfg.badge}`}>
          {pcfg.label}
        </span>
        {pend.sub && <span className="ml-1 text-[9px] text-slate-400">{pend.sub}</span>}
      </div>
      {/* 9 confiança */}
      <div className="text-right pr-2 tabular-nums text-slate-600">{conf}</div>
      {/* 10 data */}
      <div className="text-right pr-2 text-slate-500 tabular-nums">{dataRelativa(email.data_envio)}</div>
      {/* 11 ações */}
      <div className="flex items-center justify-end gap-1 pr-1">
        {podeConfirmar && (
          <button
            onClick={() => onConfirmar(email)}
            title="Confirmar empresa sugerida (resolve)"
            className="p-1 rounded text-emerald-600 hover:bg-emerald-50"
          >
            <CheckCircle2 size={15} />
          </button>
        )}
        <button onClick={() => onIgnorar(email)} title="Ignorar" className="p-1 rounded text-slate-500 hover:bg-slate-100">
          <EyeOff size={15} />
        </button>
        <button onClick={() => onLixo(email)} title="Lixo" className="p-1 rounded text-red-500 hover:bg-red-50">
          <Trash2 size={15} />
        </button>
        <button onClick={() => onAbrir(email)} title="Abrir" className="p-1 rounded text-blue-600 hover:bg-blue-50">
          <Eye size={15} />
        </button>
      </div>
    </div>
  );
};

// Cabeçalho de coluna ordenável (tri-state: asc → desc → limpa). Mantém o GRID.
const HeaderCell = ({ label, sortKey, sort, onSort, align = 'left' }) => {
  const active = sort?.key === sortKey;
  const Arrow = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : null;
  const justify = align === 'right' ? 'justify-end pr-2' : align === 'center' ? 'justify-center' : '';
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      title={`Ordenar por ${label}`}
      className={`flex items-center gap-0.5 h-full w-full min-w-0 text-[10px] font-semibold uppercase tracking-wide hover:text-slate-800 ${justify} ${
        active ? 'text-slate-800' : 'text-slate-500'
      }`}
    >
      <span className="truncate">{label}</span>
      {Arrow && <Arrow size={11} className="flex-shrink-0" />}
    </button>
  );
};

// ── Página ──────────────────────────────────────────────────────────────────

export default function InboxTriagePage() {
  const { user } = useAuth();
  const {
    emailsPendentes,
    stats,
    loading,
    error,
    refetch,
    dominiosEmpresa,
    aplicarVinculosCompletos,
    marcarComoIgnorado,
    marcarComoLixo,
  } = useEmailsPendentes(TRIAGE_LIMIT);

  const sel = useBulkSelection();

  // Filtros (SPEC §7)
  const [busca, setBusca] = useState('');
  const [fDirecao, setFDirecao] = useState('todas'); // todas | entrada | saida
  const [fPendencia, setFPendencia] = useState('todas'); // todas | VINCULAR | CONFIRMAR | REVISAR
  const [fOrigem, setFOrigem] = useState('todas'); // todas | um dos 6 baldes
  const [fEmpresa, setFEmpresa] = useState('');

  const [localError, setLocalError] = useState(null);
  const [previewVinculo, setPreviewVinculo] = useState(null); // snapshot do gate VINCULAR
  const [drilldownOpen, setDrilldownOpen] = useState(false); // modal de triage atual (SPEC §10)
  const [sort, setSort] = useState(null); // { key, dir: 'asc'|'desc' } | null (null = default)

  // Virtualização
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setViewportH(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dominioEmpresaMap = useMemo(() => buildDominioEmpresaMap(dominiosEmpresa), [dominiosEmpresa]);

  // Conjunto filtrado + ordenado (default: pendência asc, depois data_envio desc).
  const rows = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const eq = fEmpresa.trim().toLowerCase();
    const list = (emailsPendentes || []).filter((e) => {
      if (fDirecao !== 'todas' && e.direcao !== fDirecao) return false;
      if (fPendencia !== 'todas' && pendenciaPrincipal(e).key !== fPendencia) return false;
      if (fOrigem !== 'todas' && origemVinculo(e.classificado_por) !== fOrigem) return false;
      if (eq && !(e.empresas?.empresa || '').toLowerCase().includes(eq)) return false;
      if (q) {
        const tos = Array.isArray(e.to_emails) ? e.to_emails.join(' ') : '';
        const hay = `${e.subject || ''} ${e.from_email || ''} ${tos}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      // Primário: coluna clicada (se houver sort) ou o default (pendência asc).
      if (sort) {
        const primary = sortComparator(sort, a, b);
        if (primary !== 0) return primary;
      } else {
        const oa = PENDENCIAS[pendenciaPrincipal(a).key].order;
        const ob = PENDENCIAS[pendenciaPrincipal(b).key].order;
        if (oa !== ob) return oa - ob;
      }
      // Desempate final: data_envio desc (idêntico ao default de hoje).
      return new Date(b.data_envio || 0) - new Date(a.data_envio || 0);
    });
    return list;
  }, [emailsPendentes, busca, fEmpresa, fDirecao, fPendencia, fOrigem, sort]);

  // Ids selecionados elegíveis a CONFIRMAR (empresa + ai_sugerido).
  const eligibleConfirmarIds = useMemo(() => {
    const byId = new Map((emailsPendentes || []).map((e) => [e.id, e]));
    return Array.from(sel.selectedIds).filter((id) => {
      const e = byId.get(id);
      return e && e.empresa_id && e.classificacao_status === 'ai_sugerido';
    });
  }, [sel.selectedIds, emailsPendentes]);

  // Janela visível
  const total = rows.length;
  const totalHeight = total * ROW_H;
  const startIndex = Math.max(0, Math.floor((scrollTop - HEADER_H) / ROW_H) - OVERSCAN);
  const endIndex = Math.min(total, Math.ceil((scrollTop + viewportH - HEADER_H) / ROW_H) + OVERSCAN);
  const visible = rows.slice(startIndex, endIndex);

  // Trocar qualquer filtro limpa a seleção (evita agir sobre linhas ocultas).
  const onFiltro = (setter) => (value) => {
    setter(value);
    sel.deselectAll();
  };

  // Clique no cabeçalho cicla tri-state: asc → desc → null (volta ao default).
  // NÃO mexe na seleção (só reordena os dados já carregados).
  const cycleSort = (key) => {
    if (!SORTABLE.includes(key)) return; // "Ações"/seleção não ordenam
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  // ── Ações em lote — reusam EXATAMENTE os métodos do hook (skipRefetch + 1 fetch)

  const abrirPreviewVinculo = () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const byId = new Map((emailsPendentes || []).map((e) => [e.id, e]));
    const emails = ids.map((id) => byId.get(id)).filter(Boolean);
    setPreviewVinculo(computeVinculoPreview(emails, dominioEmpresaMap));
  };

  // Apply do gate VINCULAR: grava SOMENTE empresa_id, via o caminho existente
  // (aplicarVinculosCompletos). NUNCA toca contato_id. SPEC §9.2/§9.3.
  const handleConfirmVinculo = async () => {
    if (!previewVinculo) return;
    for (const grupo of previewVinculo.grupos) {
      for (const emailId of grupo.emailIds) {
        await aplicarVinculosCompletos(emailId, { empresa_id: grupo.empresa_id }, user?.email, { skipRefetch: true });
      }
    }
    await refetch();
    setLocalError(null);
    sel.deselectAll();
    setPreviewVinculo(null);
  };

  // CONFIRMAR (lote): só linhas com empresa + ai_sugerido → resolve mantendo a
  // empresa da IA. aplicarVinculosCompletos({resolver:true}) — não toca contato.
  const bulkConfirmar = async () => {
    const ids = eligibleConfirmarIds;
    if (ids.length === 0) {
      setLocalError('Nenhum dos selecionados é "Confirmar" (precisa de empresa sugerida pela IA).');
      return;
    }
    for (const id of ids) {
      await aplicarVinculosCompletos(id, { resolver: true }, user?.email, { skipRefetch: true });
    }
    await refetch();
    setLocalError(null);
  };

  const bulkIgnorar = async () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    for (const id of ids) await marcarComoIgnorado(id, user?.email, { skipRefetch: true });
    await refetch();
    setLocalError(null);
  };

  const bulkLixo = async () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    for (const id of ids) await marcarComoLixo(id, user?.email, { skipRefetch: true });
    await refetch();
    setLocalError(null);
  };

  // ── Ações por linha (op única → refetch padrão do método)
  const rowConfirmar = async (email) => {
    try { await aplicarVinculosCompletos(email.id, { resolver: true }, user?.email); }
    catch (e) { setLocalError(e.message); }
  };
  const rowIgnorar = async (email) => {
    try { await marcarComoIgnorado(email.id, user?.email); }
    catch (e) { setLocalError(e.message); }
  };
  const rowLixo = async (email) => {
    try { await marcarComoLixo(email.id, user?.email); }
    catch (e) { setLocalError(e.message); }
  };
  const abrirDrill = () => setDrilldownOpen(true);

  const capWarning = !loading && (emailsPendentes?.length || 0) < (stats?.total || 0);

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <AppHeader title="Inbox · Triage" subtitle="Matriz de proveniência (Inbox 2.0)" icon={Inbox} />

      {/* Barra de filtros + contador */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white px-4 py-2 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={busca}
              onChange={(e) => onFiltro(setBusca)(e.target.value)}
              placeholder="Buscar assunto, remetente, destinatário…"
              className="pl-7 pr-2 py-1 text-xs border border-slate-300 rounded w-72"
            />
          </div>

          <select
            value={fDirecao}
            onChange={(e) => onFiltro(setFDirecao)(e.target.value)}
            className="text-xs px-2 py-1 border border-slate-300 rounded"
          >
            <option value="todas">Direção: todas</option>
            <option value="entrada">Entrada</option>
            <option value="saida">Saída</option>
          </select>

          <select
            value={fPendencia}
            onChange={(e) => onFiltro(setFPendencia)(e.target.value)}
            className="text-xs px-2 py-1 border border-slate-300 rounded"
          >
            <option value="todas">Pendência: todas</option>
            <option value="VINCULAR">Vincular</option>
            <option value="CONFIRMAR">Confirmar</option>
            <option value="REVISAR">Revisar</option>
          </select>

          <select
            value={fOrigem}
            onChange={(e) => onFiltro(setFOrigem)(e.target.value)}
            className="text-xs px-2 py-1 border border-slate-300 rounded"
          >
            <option value="todas">Origem: todas</option>
            {ORIGEM_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>

          <input
            value={fEmpresa}
            onChange={(e) => onFiltro(setFEmpresa)(e.target.value)}
            placeholder="Empresa contém…"
            className="text-xs px-2 py-1 border border-slate-300 rounded w-44"
          />

          <button
            onClick={refetch}
            className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1 ml-auto"
            title="Recarregar fila"
          >
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <SelectAllCheckbox
            totalCount={rows.length}
            selectedCount={sel.count}
            onSelectAll={() => sel.selectAll(rows.map((r) => r.id))}
            onDeselectAll={sel.deselectAll}
            noun="filtrados"
          />
          <span>
            <strong className="text-slate-700">{sel.count}</strong> selecionados ·{' '}
            <strong className="text-slate-700">{rows.length}</strong> filtrados ·{' '}
            <strong className="text-slate-700">{stats?.total ?? 0}</strong> na fila
          </span>
          {capWarning && (
            <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
              Mostrando {emailsPendentes.length} de {stats.total} — aumente o teto de leitura.
            </span>
          )}
        </div>
      </div>

      {(error || localError) && (
        <div className="flex-shrink-0 mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={13} />
          <span className="text-xs text-red-800 flex-1">{error || localError}</span>
          {localError && (
            <button onClick={() => setLocalError(null)} className="text-red-400 hover:text-red-700">
              <X size={13} />
            </button>
          )}
        </div>
      )}

      {/* Matriz: scroll horizontal externo; cabeçalho sticky + corpo virtualizado */}
      <div ref={scrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} className="flex-1 min-h-0 overflow-auto">
        <div style={{ minWidth: GRID_MIN_W }}>
          {/* Cabeçalho */}
          <div
            className={`${GRID} sticky top-0 z-10 bg-slate-100 border-b border-slate-200 px-1 text-[10px] uppercase tracking-wide text-slate-500 font-semibold`}
            style={{ height: HEADER_H }}
          >
            <div />
            <HeaderCell label="Dir" sortKey="dir" sort={sort} onSort={cycleSort} align="center" />
            <HeaderCell label="Assunto" sortKey="assunto" sort={sort} onSort={cycleSort} />
            <HeaderCell label="Remetente" sortKey="remetente" sort={sort} onSort={cycleSort} />
            <HeaderCell label="Destinatário" sortKey="destinatario" sort={sort} onSort={cycleSort} />
            <HeaderCell label="Empresa" sortKey="empresa" sort={sort} onSort={cycleSort} />
            <HeaderCell label="Origem do vínculo" sortKey="origem" sort={sort} onSort={cycleSort} />
            <HeaderCell label="Pendência" sortKey="pendencia" sort={sort} onSort={cycleSort} />
            <HeaderCell label="Conf." sortKey="conf" sort={sort} onSort={cycleSort} align="right" />
            <HeaderCell label="Data" sortKey="data" sort={sort} onSort={cycleSort} align="right" />
            <div className="text-right pr-1">Ações</div>
          </div>

          {/* Corpo */}
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-500">Carregando fila…</div>
          ) : total === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              {(emailsPendentes?.length || 0) === 0 ? 'Inbox limpa — nenhum pendente.' : 'Nenhum email neste filtro.'}
            </div>
          ) : (
            <div style={{ height: totalHeight, position: 'relative' }}>
              {visible.map((email, i) => {
                const index = startIndex + i;
                return (
                  <TriageRow
                    key={email.id}
                    email={email}
                    style={{ position: 'absolute', top: index * ROW_H, left: 0, right: 0, height: ROW_H }}
                    selected={sel.isSelected(email.id)}
                    onToggle={sel.toggle}
                    onAbrir={abrirDrill}
                    onConfirmar={rowConfirmar}
                    onIgnorar={rowIgnorar}
                    onLixo={rowLixo}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Barra de ações em lote */}
      <BulkActionBar
        count={sel.count}
        onClear={sel.deselectAll}
        actions={[
          {
            label: 'Vincular empresa',
            icon: Building2,
            color: 'blue',
            // SEM confirmMessage — abre o gate de preview (D-11).
            onClick: () => { abrirPreviewVinculo(); },
          },
          {
            label: `Confirmar${eligibleConfirmarIds.length ? ` (${eligibleConfirmarIds.length})` : ''}`,
            icon: CheckCircle2,
            color: 'green',
            confirmMessage: () =>
              eligibleConfirmarIds.length
                ? `Confirmar ${eligibleConfirmarIds.length} email(s) com empresa sugerida pela IA? (os demais selecionados são ignorados nesta ação)`
                : 'Nenhum dos selecionados é "Confirmar". Selecione linhas com empresa sugerida pela IA.',
            onClick: bulkConfirmar,
          },
          {
            label: 'Ignorar',
            icon: EyeOff,
            color: 'gray',
            confirmMessage: (n) => `Ignorar ${n} email${n === 1 ? '' : 's'}?`,
            onClick: bulkIgnorar,
          },
          {
            label: 'Lixo',
            icon: Trash2,
            color: 'red',
            confirmMessage: (n) => `Mover ${n} email${n === 1 ? '' : 's'} para o lixo?`,
            onClick: bulkLixo,
          },
        ]}
      />

      {/* Gate VINCULAR (reuso da Fase 0) */}
      {previewVinculo && (
        <BulkVincularEmpresaModal
          preview={previewVinculo}
          onConfirm={handleConfirmVinculo}
          onClose={() => setPreviewVinculo(null)}
        />
      )}

      {/* Drill-down: modal de triage atual, intacto (SPEC §10) */}
      {drilldownOpen && (
        <EmailsPendentesModal onClose={() => { setDrilldownOpen(false); refetch(); }} />
      )}
    </div>
  );
}
